import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import { RefreshCw, AlertTriangle, TrendingDown, CheckCircle, Monitor, X, Sparkles, Search, Table, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, Legend } from 'recharts';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';
import Badge from '../components/Badge';
import { useAIFeatures } from '../hooks/useAIFeatures';
import { useAuth } from '../context/AuthContext';
import CompanyDeptFilter from '../components/CompanyDeptFilter';

const CATEGORY_ICONS = {
  mobile_phone:'📱', laptop:'💻', desktop:'🖥️', printer:'🖨️', scanner:'📷',
  monitor:'🖥️', tv:'📺', accessory:'📦', networking:'🌐', server:'🔲',
  tablet:'📱', smartwatch:'⌚', other:'📌'
};

const DT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0d1528', border: '1px solid rgba(59,130,246,0.4)', borderRadius: 10, padding: '10px 14px' }}>
      {label && <div style={{ color: '#64748b', fontSize: 11, marginBottom: 6 }}>{label}</div>}
      {payload.map((p, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.fill || p.color || '#60a5fa' }}/>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{p.name}:</span>
        <span style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 700 }}>{p.value}</span>
      </div>)}
    </div>
  );
};

const ax = { fill: '#1e3a5f', fontSize: 10 };
const AGE_COLORS = ['#10b981','#3b82f6','#f59e0b','#f97316','#ef4444','#dc2626'];

// ── Detail Popup ───────────────────────────────────────────────────────────
function DetailPopup({ title, subtitle, color, assets, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,8,16,0.88)', backdropFilter: 'blur(10px)', animation: 'fadeIn 0.2s ease-out' }}>
      <div className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(160deg,#0d1528,#090e1c)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.85)', animation: 'slideUp 0.3s ease-out' }}>
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <div className="font-bold text-sm" style={{ color: '#e2e8f0' }}>{title}</div>
            <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{subtitle} · {assets.length} assets</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>{assets.length} records</span>
            <button onClick={onClose} className="btn-ghost p-1.5"><X size={15}/></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1" style={{ padding: '4px 6px 8px' }}>
          {assets.length === 0 ? (
            <div className="flex items-center justify-center h-32" style={{ color: '#1e3a5f' }}>No records</div>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Asset #','Brand / Model','Dept','Holder','Purchased','Age','Status'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assets.map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#60a5fa', whiteSpace: 'nowrap' }}>{a.asset_number}</td>
                    <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{a.brand} {a.model}</td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.department || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.holder_name || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{a.purchased_date || '—'}</td>
                    <td style={{ padding: '8px 12px' }}><span style={{ color: parseFloat(a.age_years) >= 4 ? '#f87171' : '#94a3b8', fontWeight: 600 }}>{a.age_years ? `${a.age_years}y` : '—'}</span></td>
                    <td style={{ padding: '8px 12px' }}><Badge value={a.status}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Clickable stat card ────────────────────────────────────────────────────
const StatCard = ({ label, value, color, icon: Icon, onClick, sub }) => (
  <div onClick={onClick} className={`card-3d glass glow-border rounded-2xl p-4 relative overflow-hidden ${onClick ? 'cursor-pointer group' : ''}`}>
    <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full pointer-events-none" style={{ background: color, opacity: 0.07, filter: 'blur(12px)' }}/>
    <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}18`, border: `1px solid ${color}35` }}>
      <Icon size={15} style={{ color }}/>
    </div>
    <div className="text-xl font-bold" style={{ color: '#f1f5f9' }}>{value ?? '—'}</div>
    <div className="text-xs" style={{ color: '#475569' }}>{label}</div>
    {sub && <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{sub}</div>}
    {onClick && <div className="absolute bottom-2 right-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#334155' }}>click for details</div>}
  </div>
);

export default function Analysis() {
  const { isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('mobile_phone');
  const [analysisType, setAnalysisType] = useState('assets');
  const [data, setData] = useState(null);
  const [accData, setAccData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [accPopup, setAccPopup] = useState(null);
  const [accPopupData, setAccPopupData] = useState([]);
  const [accPopupLoading, setAccPopupLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]);
  const [filterDept, setFilterDept] = useState(null);
  const aiFeatures = useAIFeatures();
  // AI NL Query state
  const [nlQuestion, setNlQuestion] = useState('');
  const [nlResult, setNlResult] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState('');

  const runNlQuery = async () => {
    if (!nlQuestion.trim()) return;
    setNlLoading(true); setNlError(''); setNlResult(null);
    try {
      const r = await api.post('/ai/nl-query', { question: nlQuestion });
      setNlResult(r.data);
    } catch (e) {
      setNlError(e.response?.data?.error || 'Query failed');
    } finally { setNlLoading(false); }
  };

  useEffect(() => {
    api.get('/analysis/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const loadAnalysis = useCallback(() => {
    if (analysisType === 'insights') {
      setLoading(true);
      const params = {};
      if (filterCompany.length) params.company_id = filterCompany.join(',');   // every other tab does this
      if (filterDept) params.department_id = filterDept;
      api.get('/analysis/insights', { params })
        .then(r => setInsights(r.data)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    if (analysisType === 'accessories') {
      setLoading(true);
      const params = {};
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterDept) params.department_id = filterDept;
      api.get('/analysis/accessories', { params })
        .then(r => setAccData(r.data)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    if (!selected) return;
    setLoading(true);
    const params = { category: selected === 'all' ? '' : selected };
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(",") : filterCompany;
    if (filterDept) params.department_id = filterDept;
    api.get('/analysis', { params })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [selected, dateFrom, dateTo, filterCompany, filterDept, analysisType]);

  useEffect(() => { loadAnalysis(); }, [loadAnalysis]);

  // Open popup with filtered asset list
  const openPopup = async (title, subtitle, color, ageFilter) => {
    setPopupLoading(true);
    setPopup({ title, subtitle, color, assets: [], loading: true });
    try {
      const cat = selected === 'all' ? '' : selected;
      // Fetch from analysis eolAssets or directly from assets API
      const [rows] = await (async () => {
        const r = await api.get('/analysis', { params: { category: cat } });
        return [r.data];
      })();
      
      let assets = [];
      if (ageFilter === 'eol') assets = rows.eolAssets || [];
      else if (ageFilter === 'warranty') {
        // Filter from full list - use assets API
        const r = await api.get('/assets', { params: { category: cat || undefined, limit: 100, depreciation_status: 'active' } });
        assets = (r.data.data || [])
          .filter(a => a.purchased_date && (Date.now() - new Date(a.purchased_date)) / 86400000 <= 365)
          .map(a => ({
            asset_number: a.asset_number, brand: a.brand, model: a.model,
            department: a.Department?.name, holder_name: a.currentHolder?.full_name,
            purchased_date: a.purchased_date, status: a.status,
            age_years: a.purchased_date ? ((Date.now() - new Date(a.purchased_date)) / (365.25*86400000)).toFixed(1) : null
          }));
      } else if (ageFilter === 'dept' || ageFilter === 'brand') {
        assets = rows.eolAssets || [];
      } else {
        assets = rows.eolAssets || [];
      }
      setPopup({ title, subtitle, color, assets });
    } catch {} finally { setPopupLoading(false); }
  };

  // Open accessories popup
  const openAccPopup = async (title, statusFilter) => {
    setAccPopup(title);
    setAccPopupLoading(true);
    setAccPopupData([]);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/accessories', { params });
      setAccPopupData(r.data.data || []);
    } catch {} finally { setAccPopupLoading(false); }
  };

  const stats = data?.ageStats || {};
  const totalReplacementCost = (data?.replacementCost || []).reduce((s, r) => s + parseFloat(r.replacement_cost_with_margin||0), 0);
  const totalReplacementMargin = (data?.replacementCost || []).reduce((s, r) => s + parseFloat(r.margin_10pct||0), 0);
  const fmtLKR = (v) => v != null && parseFloat(v) > 0 ? `LKR ${Number(Math.round(parseFloat(v))).toLocaleString()}` : '—';

  const accSummary = accData?.summary || {};

  return (
    <div className="space-y-4">
      <PageHeader title="Asset Analysis">
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && (
            <CompanyDeptFilter
              companyId={filterCompany}
              deptId={filterDept}
              onChange={(c, d) => { setFilterCompany(c); setFilterDept(d); }}
            />
          )}
          <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t)=>{setDateFrom(f);setDateTo(t);}}/>
          <button onClick={loadAnalysis} className="btn-ghost p-2"><RefreshCw size={13} className={loading?'animate-spin':''}/></button>
        </div>
      </PageHeader>

      {/* Assets / Accessories tab toggle */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)'}}>
        {[['assets','Assets'],['accessories','Accessories'],['insights','Insights']].map(([key,label]) => (
          <button key={key} onClick={() => setAnalysisType(key)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{background:analysisType===key?'rgba(59,130,246,0.2)':'transparent',color:analysisType===key?'#60a5fa':'#475569',border:analysisType===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent'}}>
            {key==='assets'?'📊':key==='accessories'?'📦':'💡'} {label}
          </button>
        ))}
      </div>

      {/* ── MANAGEMENT INSIGHTS ──────────────────────────── */}
      {analysisType === 'insights' && (
        <div className="space-y-4">
          {loading || !insights ? (
            <div className="flex items-center justify-center h-64 gap-3" style={{color:'#334155'}}>
              <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
              Generating insights...
            </div>
          ) : (() => {
            const th = (t) => <th key={t} style={{padding:'8px 10px',textAlign:'left',color:'#475569',fontSize:11,textTransform:'uppercase',letterSpacing:'0.05em',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>{t}</th>;
            const td = (v, c, mono) => <td style={{padding:'7px 10px',color:c||'#94a3b8',fontSize:12,borderBottom:'1px solid rgba(255,255,255,0.04)',fontFamily:mono?'monospace':undefined}}>{v ?? '—'}</td>;
            const Panel = ({ title, color, children, note }) => (
              <div className="glass glow-border rounded-2xl p-5">
                <div className="text-sm font-semibold mb-1" style={{color}}>{title}</div>
                {note && <div className="text-xs mb-3" style={{color:'#475569'}}>{note}</div>}
                <div style={{overflowX:'auto'}}>{children}</div>
              </div>
            );
            const months = [...new Set((insights.heatmap||[]).map(h => h.month))].sort();
            const hmCats = [...new Set((insights.heatmap||[]).map(h => h.category))];
            const hmVal = (cat, m) => parseInt((insights.heatmap||[]).find(h => h.category===cat && h.month===m)?.count || 0);
            const hmMax = Math.max(1, ...(insights.heatmap||[]).map(h => parseInt(h.count)));
            const forecastTotal = (insights.replacementForecast||[]).reduce((s,r)=>s+parseFloat(r.est_replacement_cost||0),0);
            return (
              <>
                {/* SLA compliance */}
                <Panel title="⏱ IT Support SLA Compliance (last 6 months)" color="#60a5fa" note="Resolution speed per IT staff member — % of tickets resolved within 24h / 48h.">
                  {insights.sla?.length ? (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Staff Member','Resolved','Avg Time','Within 24h','Within 48h'].map(th)}</tr></thead>
                      <tbody>{insights.sla.map((s,i)=>(
                        <tr key={i}>
                          {td(s.full_name,'#e2e8f0')}{td(s.resolved_count)}{td(s.avg_hours!=null?`${s.avg_hours}h`:'—')}
                          {td(`${s.within_24h_pct}%`, s.within_24h_pct>=70?'#34d399':s.within_24h_pct>=40?'#fbbf24':'#f87171')}
                          {td(`${s.within_48h_pct}%`, s.within_48h_pct>=85?'#34d399':s.within_48h_pct>=60?'#fbbf24':'#f87171')}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No resolved tickets in the last 6 months yet.</div>}
                </Panel>

                {/* Ticket heatmap */}
                <Panel title="🔥 Ticket Heatmap — Category × Month (last 12 months)" color="#f59e0b" note="Darker cells = more tickets. Recurring hot spots indicate systemic problems worth fixing at the root.">
                  {hmCats.length ? (
                    <table style={{borderCollapse:'collapse'}}>
                      <thead><tr>{th('Category')}{months.map(m=>th(m.slice(2)))}</tr></thead>
                      <tbody>{hmCats.map((cat,i)=>(
                        <tr key={i}>
                          {td(cat?.replace(/_/g,' '),'#e2e8f0')}
                          {months.map(m=>{
                            const v = hmVal(cat,m);
                            const alpha = v ? 0.15 + 0.55*(v/hmMax) : 0;
                            return <td key={m} style={{padding:'6px 10px',textAlign:'center',fontSize:12,fontWeight:700,borderBottom:'1px solid rgba(255,255,255,0.04)',background:v?`rgba(245,158,11,${alpha})`:'transparent',color:v?'#fff':'#1e293b'}}>{v||''}</td>;
                          })}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No ticket data in the last 12 months yet.</div>}
                </Panel>

                {/* Replacement forecast */}
                <Panel title="📅 Replacement Forecast — next 15 months" color="#34d399"
                  note={`Assets reaching end of life soon. Estimated replacement cost includes a 10% margin. ${forecastTotal>0?`Total: LKR ${Math.round(forecastTotal).toLocaleString()}`:''}`}>
                  {insights.replacementForecast?.length ? (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Category','Assets Reaching EOL','Original Cost','Est. Replacement (+10%)'].map(th)}</tr></thead>
                      <tbody>{insights.replacementForecast.map((r,i)=>(
                        <tr key={i}>
                          {td(r.category?.replace(/_/g,' '),'#e2e8f0')}{td(r.count)}
                          {td(fmtLKR(r.original_cost))}{td(fmtLKR(r.est_replacement_cost),'#34d399')}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No assets reaching end of life within the next 15 months.</div>}
                </Panel>

                {/* Repair vs replace */}
                <Panel title="🔧 Repair vs Replace — high-maintenance assets" color="#f87171" note="Assets whose accumulated repair costs exceed 30% of purchase price (or 3+ repairs). Replacing these may be cheaper than continuing to repair.">
                  {insights.replaceCandidates?.length ? (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Asset','Category','Holder','Purchase Price','Current WDV','Repairs','Total Repair Cost'].map(th)}</tr></thead>
                      <tbody>{insights.replaceCandidates.map((r,i)=>(
                        <tr key={i}>
                          {td(`${r.asset_number} · ${r.brand||''} ${r.model||''}`,'#e2e8f0')}
                          {td(r.category?.replace(/_/g,' '))}{td(r.holder)}
                          {td(fmtLKR(r.purchase_price))}{td(fmtLKR(r.wdv))}{td(r.repair_count)}
                          {td(fmtLKR(r.total_repair_cost),'#f87171')}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No high-maintenance assets detected — repair costs are within normal limits.</div>}
                </Panel>

                {/* Cost per department */}
                <Panel title="🏢 IT Cost per Department" color="#c084fc" note="Total asset value held by each department and the equivalent IT investment per employee.">
                  {insights.costPerDept?.length ? (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Company','Department','Headcount','Assets','Asset Value','Per Employee'].map(th)}</tr></thead>
                      <tbody>{insights.costPerDept.map((r,i)=>(
                        <tr key={i}>
                          {td(r.company,'#e2e8f0')}{td(r.department||'—')}{td(r.headcount)}{td(r.asset_count)}
                          {td(fmtLKR(r.asset_value))}{td(fmtLKR(r.cost_per_employee),'#c084fc')}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No department asset data yet.</div>}
                </Panel>

                {/* Repeat offenders */}
                <Panel title="⚠ Repeat Damage Records" color="#fbbf24" note="Employees with 2 or more damage cost records — consider additional care guidance or liability review.">
                  {insights.repeatOffenders?.length ? (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Employee','Company','Damage Records','Total Cost'].map(th)}</tr></thead>
                      <tbody>{insights.repeatOffenders.map((r,i)=>(
                        <tr key={i}>
                          {td(`${r.full_name}${r.employee_code?` (${r.employee_code})`:''}`,'#e2e8f0')}
                          {td(r.company)}{td(r.damage_count,'#fbbf24')}{td(fmtLKR(r.total_cost),'#f87171')}
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No repeat damage records — good news.</div>}
                </Panel>
              </>
            );
          })()}
        </div>
      )}

      {/* ── ACCESSORIES ANALYSIS ─────────────────────────── */}
      {analysisType === 'accessories' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64 gap-3" style={{color:'#334155'}}>
              <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
              Analyzing accessories...
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Accessories" value={accSummary.total} color="#c084fc" icon={Package} onClick={() => openAccPopup('All Accessories', '')}/>
                <StatCard label="In Use" value={accSummary.in_use} color="#3b82f6" icon={CheckCircle} onClick={() => openAccPopup('Accessories In Use', 'in_use')}/>
                <StatCard label="Available" value={accSummary.available} color="#10b981" icon={CheckCircle} onClick={() => openAccPopup('Available Accessories', 'available')}/>
                <StatCard label="Disposed" value={accSummary.disposed} color="#f87171" icon={AlertTriangle} onClick={() => openAccPopup('Disposed Accessories', 'disposed')}/>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Category breakdown */}
                <div className="glass glow-border rounded-2xl p-5">
                  <div className="text-sm font-semibold mb-3" style={{color:'#94a3b8'}}>By Category</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={(accData?.categoryBreakdown||[]).map(c=>({name:c.category||'Unknown',total:parseInt(c.total),inUse:parseInt(c.in_use||0),available:parseInt(c.available||0)}))} margin={{left:-15}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                      <XAxis dataKey="name" tick={{...ax,fontSize:9}}/>
                      <YAxis tick={ax}/>
                      <Tooltip content={<DT/>}/>
                      <Legend wrapperStyle={{color:'#475569',fontSize:11}}/>
                      <Bar dataKey="inUse" name="In Use" fill="#3b82f6" stackId="s"/>
                      <Bar dataKey="available" name="Available" fill="#10b981" stackId="s" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Brand distribution */}
                <div className="glass glow-border rounded-2xl p-5">
                  <div className="text-sm font-semibold mb-3" style={{color:'#94a3b8'}}>Brand Distribution</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={(accData?.brandStats||[]).slice(0,8).map(b=>({name:b.brand,count:parseInt(b.count)}))} layout="vertical" margin={{left:0,right:10}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false}/>
                      <XAxis type="number" tick={ax}/><YAxis type="category" dataKey="name" tick={{...ax,fontSize:10}} width={80}/>
                      <Tooltip content={<DT/>}/>
                      <Bar dataKey="count" name="Count" fill="#c084fc" radius={[0,4,4,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Holders */}
              {(accData?.topHolders||[]).length > 0 && (
                <div className="glass glow-border rounded-2xl p-5">
                  <div className="text-sm font-semibold mb-3" style={{color:'#94a3b8'}}>Top Accessory Holders</div>
                  <div className="overflow-x-auto">
                    <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                      <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                        {['Employee','Designation','Accessories'].map(h=>(
                          <th key={h} style={{padding:'7px 12px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase'}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {(accData.topHolders||[]).map((r,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                            <td style={{padding:'8px 12px',color:'#e2e8f0',fontWeight:500}}>{r.full_name}</td>
                            <td style={{padding:'8px 12px',color:'#475569'}}>{r.designation||'—'}</td>
                            <td style={{padding:'8px 12px',color:'#c084fc',fontWeight:700}}>{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── ASSETS ANALYSIS (original) ──────────────────── */}
      {analysisType === 'assets' && <>

      {/* Category selector */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setSelected('all')}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all"
          style={{ background: selected==='all'?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.03)', color: selected==='all'?'#60a5fa':'#475569', border: selected==='all'?'1px solid rgba(59,130,246,0.3)':'1px solid rgba(255,255,255,0.06)' }}>
          📊 All Assets
        </button>
        {categories.map(cat => (
          <button key={cat.category} onClick={() => setSelected(cat.category)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all"
            style={{ background: selected===cat.category?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.03)', color: selected===cat.category?'#60a5fa':'#475569', border: selected===cat.category?'1px solid rgba(59,130,246,0.3)':'1px solid rgba(255,255,255,0.06)' }}>
            <span>{CATEGORY_ICONS[cat.category]||'📋'}</span>
            <div className="flex flex-col items-start">
              <span className="capitalize">{cat.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              {parseFloat(cat.total_value) > 0 && <span className="text-xs" style={{ color: selected===cat.category?'#93c5fd':'#334155' }}>{fmtLKR(cat.total_value)}</span>}
            </div>
            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: '#334155' }}>{cat.total}</span>
            {parseInt(cat.eol_pct) > 30 && <AlertTriangle size={11} style={{ color: '#f97316' }}/>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 gap-3" style={{ color: '#334155' }}>
          <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
          Analyzing...
        </div>
      ) : data && (
        <div className="space-y-4">
          {/* Clickable summary KPI cards */}
          <div className="text-xs px-1 mb-1" style={{ color: '#1e3a5f' }}>
            Click any card to see the full asset list for that group
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Assets" value={stats.total} color="#3b82f6" icon={Monitor}
              sub={fmtLKR(stats.total_value)}
              onClick={() => openPopup('All Assets', selected === 'all' ? 'All categories' : selected.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase()), '#3b82f6', 'all')}/>
            <StatCard label="Under Warranty" value={stats.under_warranty} color="#10b981" icon={CheckCircle}
              sub="Warranty still valid"
              onClick={() => openPopup('Under Warranty Assets', 'Warranty expiry date not yet passed', '#10b981', 'warranty')}/>
            <StatCard label="End of Life" value={stats.eol_over_4years} color="#ef4444" icon={AlertTriangle}
              sub={`WDV: ${fmtLKR(stats.total_wdv)}`}
              onClick={() => openPopup('End-of-Life Assets', 'Past EOL / depreciation end date — due for replacement', '#ef4444', 'eol')}/>
            <StatCard label="Est. Replacement Cost (+10%)" value={totalReplacementCost > 0 ? `LKR ${Math.round(totalReplacementCost/1000)}K` : '—'} color="#f97316" icon={TrendingDown}
              sub={totalReplacementMargin > 0 ? `incl. LKR ${Math.round(totalReplacementMargin/1000)}K margin` : undefined}
              onClick={() => openPopup('End-of-Life Assets — Replacement Needed', 'Past EOL / depreciation end date', '#f97316', 'eol')}/>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Age distribution — clickable bars */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-2" style={{ color: '#94a3b8' }}>Asset Age Distribution</div>
              <div className="text-xs mb-3" style={{ color: '#334155' }}>Click a bar to see assets in that age group</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.ageBuckets?.map(b => ({ name: b.age_bucket, count: parseInt(b.count), raw: b })) || []} margin={{ left: -15 }}
                  onClick={e => {
                    if (e?.activePayload?.[0]) {
                      const bucket = e.activePayload[0].payload;
                      openPopup(`Age: ${bucket.name}`, `${bucket.count} assets`, AGE_COLORS[data.ageBuckets?.findIndex(b => b.age_bucket === bucket.name) % AGE_COLORS.length] || '#3b82f6', 'eol');
                    }
                  }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
                  <XAxis dataKey="name" tick={{ ...ax, fontSize: 9 }}/>
                  <YAxis tick={ax}/>
                  <Tooltip content={<DT/>}/>
                  <Bar dataKey="count" name="Assets" radius={[5,5,0,0]} cursor="pointer">
                    {data.ageBuckets?.map((b, i) => <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Brand distribution — clickable bars */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-2" style={{ color: '#94a3b8' }}>Brand Distribution</div>
              <div className="text-xs mb-3" style={{ color: '#334155' }}>Click a bar to see assets for that brand</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.brandStats?.slice(0,8).map(b => ({ name: b.brand, total: parseInt(b.count), eol: parseInt(b.eol_count) }))} layout="vertical" margin={{ left: 0, right: 10 }}
                  onClick={e => {
                    if (e?.activePayload?.[0]) {
                      const brand = e.activePayload[0].payload.name;
                      const brandEols = (data.eolAssets||[]).filter(a => a.brand === brand);
                      setPopup({ title: `Brand: ${brand}`, subtitle: 'EOL assets for this brand', color: '#3b82f6', assets: brandEols });
                    }
                  }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false}/>
                  <XAxis type="number" tick={ax}/><YAxis type="category" dataKey="name" tick={{ ...ax, fontSize: 10 }} width={70}/>
                  <Tooltip content={<DT/>}/>
                  <Legend wrapperStyle={{ color: '#475569', fontSize: 11 }}/>
                  <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[0,4,4,0]} cursor="pointer"/>
                  <Bar dataKey="eol" name="EOL" fill="#ef4444" radius={[0,4,4,0]} cursor="pointer"/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Department — clickable rows */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-2" style={{ color: '#94a3b8' }}>Department-wise Distribution</div>
              <div className="text-xs mb-3" style={{ color: '#334155' }}>Click a row to see assets for that department</div>
              <div className="overflow-x-auto">
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Department','Total','Value (LKR)','Warranty','EOL','EOL %'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.deptStats?.map((d, i) => {
                      const eolPct = d.total > 0 ? Math.round(parseInt(d.eol)/parseInt(d.total)*100) : 0;
                      const deptEols = (data.eolAssets||[]).filter(a => a.department === d.department);
                      return (
                        <tr key={i} onClick={() => setPopup({ title: `Dept: ${d.department}`, subtitle: 'EOL assets in this department', color: '#8b5cf6', assets: deptEols })}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: deptEols.length ? 'pointer' : 'default' }}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.06)'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding: '7px 10px', color: '#e2e8f0', fontWeight: 500 }}>{d.department}</td>
                          <td style={{ padding: '7px 10px', color: '#60a5fa', fontWeight: 700 }}>{d.total}</td>
                          <td style={{ padding: '7px 10px', color: '#a78bfa', fontSize: 11 }}>{fmtLKR(d.total_value)}</td>
                          <td style={{ padding: '7px 10px', color: '#10b981' }}>{d.under_warranty}</td>
                          <td style={{ padding: '7px 10px', color: d.eol > 0 ? '#f87171' : '#334155' }}>{d.eol}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 50, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                                <div style={{ width: `${eolPct}%`, height: '100%', background: eolPct>50?'#ef4444':eolPct>25?'#f97316':'#10b981' }}/>
                              </div>
                              <span style={{ color: eolPct>50?'#f87171':eolPct>25?'#fb923c':'#34d399', fontSize: 11, fontWeight: 600 }}>{eolPct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Replacement cost — clickable, with 10% margin */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-1" style={{ color: '#94a3b8' }}>Estimated Replacement Cost</div>
              <div className="text-xs mb-3" style={{ color: '#334155' }}>Original cost + 10% market inflation margin · Click a brand to see assets</div>
              <div className="space-y-2">
                {data.replacementCost?.filter(r => parseInt(r.eol_count) > 0).map((r, i) => {
                  const costWithMargin = parseFloat(r.replacement_cost_with_margin || 0);
                  const origCost = parseFloat(r.total_original_value || 0);
                  const margin = parseFloat(r.margin_10pct || 0);
                  const maxCost = Math.max(...data.replacementCost.map(x => parseFloat(x.replacement_cost_with_margin||0)));
                  const brandAssets = (data.eolAssets||[]).filter(a => a.brand === r.brand);
                  return (
                    <div key={i} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors"
                      onClick={() => setPopup({ title: `Brand: ${r.brand||'Unknown'} — EOL`, subtitle: `${r.eol_count} units need replacement`, color: '#f97316', assets: brandAssets })}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(249,115,22,0.06)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <div className="w-20 text-xs truncate" style={{ color: '#e2e8f0' }}>{r.brand || 'Unknown'}</div>
                      <div className="flex-1">
                        <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                          <div style={{ width: `${maxCost > 0 ? (costWithMargin/maxCost*100) : 0}%`, height: '100%', background: 'linear-gradient(90deg,#f97316,#ef4444)' }}/>
                        </div>
                      </div>
                      <div className="text-right" style={{ minWidth: 110 }}>
                        <div className="text-xs font-bold" style={{ color: '#fb923c' }}>{fmtLKR(costWithMargin)}</div>
                        <div className="text-xs" style={{ color: '#64748b' }}>{r.eol_count} units · +{fmtLKR(margin)} margin</div>
                      </div>
                    </div>
                  );
                })}
                {totalReplacementCost > 0 && (
                  <div className="pt-2 mt-1 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>Total (incl. 10% margin)</span>
                      <span className="font-bold" style={{ color: '#f97316' }}>LKR {Number(Math.round(totalReplacementCost)).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: '#475569' }}>10% inflation margin added</span>
                      <span className="text-xs font-semibold" style={{ color: '#fbbf24' }}>+LKR {Number(Math.round(totalReplacementMargin)).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* EOL Assets list — clickable rows */}
          {data.eolAssets?.length > 0 && (
            <div className="glass glow-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-semibold" style={{ color: '#f87171' }}>⚠️ End-of-Life Assets — {data.eolAssets.length} shown</div>
                <button onClick={() => setPopup({ title: 'All End-of-Life Assets', subtitle: 'Past EOL / depreciation end date', color: '#ef4444', assets: data.eolAssets })}
                  className="btn-ghost text-xs" style={{ color: '#60a5fa' }}>View all in popup →</button>
              </div>
              <div className="overflow-x-auto">
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Asset #','Brand / Model','Dept','Holder','Purchased','EOL Date','Age','Status'].map(h => (
                      <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.eolAssets.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#60a5fa', whiteSpace: 'nowrap' }}>{a.asset_number}</td>
                        <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{a.brand} {a.model}</td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.department||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.holder_name||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{a.purchased_date}</td>
                        <td style={{ padding: '8px 12px', color: '#fb923c', whiteSpace: 'nowrap' }}>{a.eol_date || '—'}</td>
                        <td style={{ padding: '8px 12px' }}><span style={{ color: '#f87171', fontWeight: 700 }}>{a.age_years}y</span></td>
                        <td style={{ padding: '8px 12px' }}><Badge value={a.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AI Query Assistant (Feature 5) ── */}
      {aiFeatures.nl_query && <div className="rounded-2xl p-5" style={{ background:'rgba(139,92,246,0.05)', border:'1px solid rgba(139,92,246,0.2)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} style={{ color:'#a78bfa' }}/>
          <span className="text-sm font-semibold" style={{ color:'#a78bfa' }}>AI Query Assistant</span>
          <span className="text-xs ml-1" style={{ color:'#334155' }}>Ask anything about your data in plain English</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={nlQuestion}
            onChange={e => setNlQuestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !nlLoading && runNlQuery()}
            placeholder='e.g. "How many laptops are in use at CLS?" or "Top 5 employees with most damage costs"'
            className="input-dark flex-1 text-sm"
            style={{ borderColor: 'rgba(139,92,246,0.3)' }}
          />
          <button onClick={runNlQuery} disabled={nlLoading || !nlQuestion.trim()}
            className="btn-primary flex items-center gap-2 px-4"
            style={{ background:'rgba(139,92,246,0.25)', borderColor:'rgba(139,92,246,0.4)', opacity: nlLoading ? 0.7 : 1 }}>
            {nlLoading ? <RefreshCw size={13} className="animate-spin"/> : <Search size={13}/>}
            {nlLoading ? 'Querying...' : 'Ask AI'}
          </button>
        </div>

        {/* Quick examples */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {['How many assets per company?', 'Show open tickets by priority', 'Assets older than 4 years in use', 'Licenses expiring this year'].map(q => (
            <button key={q} onClick={() => { setNlQuestion(q); }}
              className="text-xs px-2.5 py-1 rounded-full"
              style={{ background:'rgba(139,92,246,0.1)', color:'#7c3aed', border:'1px solid rgba(139,92,246,0.2)' }}>
              {q}
            </button>
          ))}
        </div>

        {nlError && (
          <div className="mt-3 p-3 rounded-xl text-xs flex items-center gap-2"
            style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171' }}>
            <AlertTriangle size={12}/>{nlError}
          </div>
        )}

        {nlResult && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Table size={12} style={{ color:'#475569' }}/>
              <span className="text-xs" style={{ color:'#475569' }}>{nlResult.count} row{nlResult.count !== 1 ? 's' : ''} returned</span>
              <code className="text-xs px-2 py-0.5 rounded ml-2 flex-1 truncate"
                style={{ background:'rgba(255,255,255,0.04)', color:'#334155', fontFamily:'monospace' }}>
                {nlResult.sql}
              </code>
            </div>
            {nlResult.rows?.length > 0 && (
              <div className="overflow-x-auto rounded-xl" style={{ border:'1px solid rgba(255,255,255,0.07)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background:'rgba(255,255,255,0.04)' }}>
                      {Object.keys(nlResult.rows[0]).map(k => (
                        <th key={k} className="px-3 py-2 text-left font-semibold"
                          style={{ color:'#334155', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>
                          {k.replace(/_/g,' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nlResult.rows.map((row, i) => (
                      <tr key={i} style={{ borderTop:'1px solid rgba(255,255,255,0.04)' }}>
                        {Object.values(row).map((v, j) => (
                          <td key={j} className="px-3 py-2" style={{ color:'#94a3b8' }}>
                            {v === null || v === undefined ? '—' : String(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      }

      {/* Detail Popup */}
      {popup && !popup.loading && (
        <DetailPopup
          title={popup.title}
          subtitle={popup.subtitle}
          color={popup.color}
          assets={popup.assets || []}
          onClose={() => setPopup(null)}
        />
      )}
      {popup?.loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(4,8,16,0.8)' }}>
          <div className="flex items-center gap-3" style={{ color: '#60a5fa' }}>
            <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
            Loading details...
          </div>
        </div>
      )}
      </>}

      {/* Accessories Popup */}
      {accPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,8,16,0.88)', backdropFilter: 'blur(10px)', animation: 'fadeIn 0.2s ease-out' }}>
          <div className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(160deg,#0d1528,#090e1c)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.85)', animation: 'slideUp 0.3s ease-out' }}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div className="font-bold text-sm" style={{ color: '#e2e8f0' }}>{accPopup}</div>
                <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{accPopupLoading ? 'Loading...' : `${accPopupData.length} accessories`}</div>
              </div>
              <button onClick={() => setAccPopup(null)} className="btn-ghost p-1.5"><X size={15}/></button>
            </div>
            <div className="overflow-y-auto flex-1" style={{ padding: '4px 6px 8px' }}>
              {accPopupLoading ? (
                <div className="flex items-center justify-center h-36 gap-3" style={{ color: '#334155' }}>
                  <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
                  <span style={{ fontSize: 13 }}>Loading accessories...</span>
                </div>
              ) : accPopupData.length === 0 ? (
                <div className="flex items-center justify-center h-36" style={{ color: '#1e3a5f' }}>No records found</div>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['ACC #','Name','Category','Brand/Model','Status','Holder','Warranty Expiry'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accPopupData.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(192,132,252,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#a78bfa', whiteSpace: 'nowrap' }}>{a.accessory_number||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{a.name||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#475569' }}>{`${a.brand||''} ${a.model||''}`.trim()||'—'}</td>
                        <td style={{ padding: '8px 12px' }}><Badge value={a.status}/></td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.assignedEmployee?.full_name||'—'}</td>
                        <td style={{ padding: '8px 12px', color: a.warranty_expiry_date && new Date(a.warranty_expiry_date) < new Date() ? '#f87171' : '#475569', whiteSpace: 'nowrap' }}>{a.warranty_expiry_date||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
